import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DateInput from "../components/DateInput";
import { PageHeader } from "../components/admin/AdminUI";

// Kode kelas yang bisa dipilih — sama seperti AttendanceGridPage.js.
const CLASS_OPTIONS = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

// Kelas yang DIDAPAT 1 Year Program — bukan pilihan. Sejajar dengan
// YEARLY_PROGRAM_CLASSES di backend/utils/classes.js; di sini hanya untuk
// ditampilkan sebagai keterangan, backend tetap yang menentukan.
const YEARLY_CLASSES = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM"];

const ROLE_IFGF = [
  "HITS/MTh Student",
  "Pastoral",
  "Staff",
  "Coach",
  "Care Group Leader (CGL)",
  "Core Team Leader (CTL)",
  "Attendees (IFGF BDG)",
];

// Nilai yang benar-benar dipakai di data untuk gereja non-IFGF Bandung —
// diambil dari isi tabel student, bukan ditebak.
const ROLE_OTHERS = ["Others - Attendees", "Others - Pastor", "Free"];

// Kunci ini yang disimpan di kolom scholarshipType; labelnya mengikuti
// scholarshipLabel di controllers/ScholarshipController.js.
const SCHOLARSHIP_OPTIONS = [
  { value: "", label: "Tidak ada" },
  { value: "need-based", label: "Opportunity Scholarship (need-based)" },
  { value: "merit", label: "Leadership Scholarship (merit)" },
];

// Lima pertanyaan umum — teksnya disalin dari form pendaftaran publik
// (components/RegisterYearly.js) supaya admin dan student membaca pertanyaan
// yang sama. Urutannya menentukan kolom question1..question5.
const GENERAL_QUESTIONS = [
  "Bagaimana kisah perjalanan kekristenan Anda, ketika Anda mengalami lahir baru hingga saat ini?",
  "Apakah Anda sudah mengalami baptisan?",
  "Ceritakan beberapa pengalaman pelayanan Anda dan buah dari pelayanan-pelayanan tersebut.",
  "Apa rencana dan langkah Anda selanjutnya setelah mengikuti Cutting Edge Academy?",
  "Mengapa Anda ingin bergabung dengan Cutting Edge Academy?",
];

// Dua jenis beasiswa memakai set pertanyaan yang BERBEDA — merit 7 pertanyaan,
// need-based 5. Teksnya disalin dari components/ApplyScholarship.js. Keduanya
// tersimpan ke kolom scholarshipQ1..Q7 sesuai urutan di sini.
const MERIT_QUESTIONS = [
  "Sejauh ini, bagaimana Anda telah berkontribusi bagi komunitas, baik di dalam maupun di luar gereja lokal? Ceritakan pengalaman pelayanan dan/atau kepemimpinan Anda hingga saat ini.",
  "Apakah sejauh ini Anda telah mengikuti kelas-kelas kepemimpinan atau pembelajaran Alkitab? Ceritakan pengalaman pembekalan yang telah Anda jalani hingga saat ini.",
  "Bagaimana Anda melihat diri Anda dalam 5 hingga 10 tahun ke depan?",
  "Mengapa Anda memilih Cutting Edge Academy sebagai musim pembekalan dalam perjalanan kepemimpinan dan penatalayanan Anda?",
  "Ceritakan tentang holy discontent yang Anda miliki.",
  "Ceritakan satu pengalaman yang paling bermakna dalam perjalanan kepemimpinan dan penatalayanan Anda.",
  "Mengapa kami seharusnya memberikan beasiswa ini kepada Anda?",
];

const OPPORTUNITY_QUESTIONS = [
  "Bagaimana Anda menggambarkan kondisi Anda saat ini?",
  "Seberapa mendesak kebutuhan Anda untuk bergabung dengan Cutting Edge Academy pada tahun ini?",
  "Berapa besar dukungan finansial yang Anda ajukan agar dapat bergabung dengan Cutting Edge Academy pada tahun ini?",
  "Ceritakan pengalaman pelayanan dan/atau pengabdian kepada masyarakat yang telah Anda jalani hingga saat ini.",
  "Mengapa kami seharusnya memberikan beasiswa ini kepada Anda?",
];

const textarea = {
  width: "100%",
  minHeight: "84px",
  padding: "10px 12px",
  fontSize: "14px",
  border: "1px solid var(--adm-border)",
  borderRadius: "8px",
  resize: "vertical",
  outline: "none",
};

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#fb3104" });

const label = { fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px", display: "block" };
const input = {
  width: "100%",
  height: "40px",
  padding: "0 12px",
  fontSize: "14px",
  border: "1px solid var(--adm-border)",
  borderRadius: "8px",
  backgroundColor: "#fff",
  outline: "none",
};
const card = {
  backgroundColor: "white",
  border: "1px solid var(--adm-border)",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "20px",
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
};
const sectionTitle = { fontSize: "15px", fontWeight: 700, color: "#231f20", marginBottom: "14px" };

const Field = ({ text, children, required }) => (
  <div className="col-12 col-md-6 col-lg-4 mb-3">
    <label style={label}>
      {text} {required && <span style={{ color: "#fb3104" }}>*</span>}
    </label>
    {children}
  </div>
);

const AddStudentPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);
  const [saving, setSaving] = useState(false);

  // "archive" = buat Student langsung, tanpa tagihan.
  // "billed"  = buat Approval untuk batch berjalan + kirim link pembayaran.
  const [mode, setMode] = useState("archive");

  const [f, setF] = useState({
    studentId: "",
    name: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    studentPackage: "",
    churchChoice: "IFGF Bandung",
    otherChurch: "",
    lastDj: "",
    role: "",
    classType: "Onsite",
    program: "1 Year Program",
    joinedCEA: "",
    tshirtSize: "",
    scholarshipType: "",
    paymentStatus: "unpaid",
    paymentPlan: "full",
    discountPercent: "0",
  });

  const [startingClass, setStartingClass] = useState("OT");
  const [answers, setAnswers] = useState(Array(GENERAL_QUESTIONS.length).fill(""));
  const [schAnswers, setSchAnswers] = useState(Array(7).fill(""));
  const [refName, setRefName] = useState("");
  const [refContact, setRefContact] = useState("");
  const [autoId, setAutoId] = useState(true);
  const [idPreview, setIdPreview] = useState(null);
  const [classes, setClasses] = useState({});
  const [allowDupId, setAllowDupId] = useState(false);
  const [dupWarning, setDupWarning] = useState("");

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const isYearly = f.program === "1 Year Program";
  const schQuestions =
    f.scholarshipType === "merit" ? MERIT_QUESTIONS : f.scholarshipType === "need-based" ? OPPORTUNITY_QUESTIONS : [];

  useEffect(() => {
    setLocationAdmin(address);
    (async () => {
      try {
        await axios.get("/meAdmin");
      } catch (error) {
        setErrorMe(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  // Pratinjau ID diambil ulang tiap program berubah, karena program menentukan
  // dua digit tengah ID (10 = 1 Year, 01 = 1 Month).
  useEffect(() => {
    if (!autoId) return;
    let cancelled = false;
    axios
      .get("/student/nextid", { params: { program: f.program } })
      .then((res) => {
        if (!cancelled) setIdPreview(res.data);
      })
      .catch(() => {
        if (!cancelled) setIdPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [autoId, f.program]);

  const toggleClass = (code) =>
    setClasses((p) => {
      const next = { ...p };
      if (next[code] !== undefined) delete next[code];
      else next[code] = "";
      return next;
    });

  const handleSubmit = async () => {
    if (!f.name.trim()) return swalError("Nama wajib diisi.");
    if (!f.email.trim()) return swalError("Email wajib diisi.");
    if (!autoId && !f.studentId.trim()) return swalError("Isi Student ID, atau centang generate otomatis.");
    // 1 Year Program kelasnya ditentukan backend (paket penuh), jadi tidak
    // ada yang perlu divalidasi di sini.
    if (!isYearly && Object.keys(classes).length === 0) return swalError("Pilih minimal satu kelas.");

    const localChurch = f.churchChoice === "Others" ? f.otherChurch.trim() : f.churchChoice;

    setSaving(true);
    setDupWarning("");
    try {
      const res = await axios.post("/student/manual", {
        mode,
        autoGenerateId: autoId,
        studentId: autoId ? "" : f.studentId.trim(),
        name: f.name.trim(),
        email: f.email.trim(),
        phoneNumber: f.phoneNumber.trim(),
        dateOfBirth: f.dateOfBirth,
        address: f.address.trim(),
        city: f.city.trim(),
        province: f.province.trim(),
        postalCode: f.postalCode.trim(),
        studentPackage: f.studentPackage,
        localChurch,
        lastDj: f.lastDj,
        role: f.role,
        classType: f.classType,
        program: f.program,
        joinedCEA: f.joinedCEA,
        tshirtSize: f.tshirtSize,
        scholarshipType: f.scholarshipType,
        paymentStatus: f.paymentStatus,
        paymentPlan: f.paymentPlan,
        discountPercent: Number(f.discountPercent) || 0,
        allowDuplicateStudentId: allowDupId,
        startingClass: isYearly ? startingClass : undefined,
        question1: answers[0],
        question2: answers[1],
        question3: answers[2],
        question4: answers[3],
        question5: answers[4],
        // Hanya dikirim kalau jenis beasiswa dipilih — kalau tidak, backend
        // tidak akan membuat baris beasiswa sama sekali.
        ...(f.scholarshipType
          ? {
              scholarshipQ1: schAnswers[0],
              scholarshipQ2: schAnswers[1],
              scholarshipQ3: schAnswers[2],
              scholarshipQ4: schAnswers[3],
              scholarshipQ5: schAnswers[4],
              scholarshipQ6: schAnswers[5],
              scholarshipQ7: schAnswers[6],
              referenceName: refName.trim(),
              referenceContact: refContact.trim(),
            }
          : {}),
        // Batch per kelas dibiarkan kosong → backend mengisinya dengan batch
        // yang sedang berjalan, sumber yang sama dengan pendaftaran normal.
        // 1 Year Program: dikirim kosong — backend mengisinya dengan paket
        // penuh YEARLY_PROGRAM_CLASSES, satu sumber kebenaran.
        classes: isYearly
          ? []
          : Object.entries(classes).map(([name, batch]) => ({
              name,
              ...(String(batch).trim() !== "" ? { batch: String(batch).trim() } : {}),
            })),
      });

      await Swal.fire({
        text: res.data.msg,
        icon: res.data.paymentFailed ? "warning" : "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
      // Mode "billed" belum menghasilkan baris Student (student dibuat setelah
      // bayar), jadi diarahkan ke halaman Approval — di situ barisnya terlihat.
      navigate(mode === "billed" ? "/approvalpage" : "/studentdatapage");
    } catch (error) {
      const msg = error.response?.data?.msg || "Gagal membuat student.";
      if (error.response?.data?.duplicateStudentId) setDupWarning(msg);
      else swalError(msg);
    } finally {
      setSaving(false);
    }
  };

  const modeBtn = (active) => ({
    flex: "1 1 260px",
    textAlign: "left",
    padding: "12px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    border: active ? "2px solid var(--adm-primary)" : "1px solid var(--adm-border)",
    backgroundColor: active ? "var(--adm-primary-soft)" : "#fff",
  });

  return (
    <div>
      <div className="adm-main">
        <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginBottom: "8px", paddingLeft: 0 }} onClick={() => navigate("/studentdatapage")}>
          <i className="bi bi-arrow-left"></i> Back to Student Data
        </button>
        <PageHeader
          title="Add Student"
          subtitle="Add a student whose data comes from outside the website registration."
        />

        {/* ── Mode ── */}
        <div style={card}>
          <p style={sectionTitle}>Jenis pemasukan data</p>
          <div className="d-flex gap-3 flex-wrap">
            <div style={modeBtn(mode === "archive")} onClick={() => setMode("archive")}>
              <label style={{ fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                <input
                  type="radio"
                  checked={mode === "archive"}
                  onChange={() => setMode("archive")}
                  style={{ marginRight: "8px" }}
                />
                Arsip — tanpa tagihan
              </label>
              <p style={{ fontSize: "12px", color: "#666", margin: "6px 0 0" }}>
                Student langsung aktif. Tidak ada tagihan dan tidak ada email. Untuk walk-in, angkatan lama, atau data
                arsip yang urusan bayarnya di luar sistem.
              </p>
            </div>

            <div style={modeBtn(mode === "billed")} onClick={() => setMode("billed")}>
              <label style={{ fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                <input
                  type="radio"
                  checked={mode === "billed"}
                  onChange={() => setMode("billed")}
                  style={{ marginRight: "8px" }}
                />
                Batch berjalan — kirim link pembayaran
              </label>
              <p style={{ fontSize: "12px", color: "#666", margin: "6px 0 0" }}>
                Masuk sebagai pendaftaran batch{" "}
                <strong>{idPreview?.batch ? idPreview.batch : "berjalan"}</strong> dan student menerima email link
                pembayaran. Baris student baru dibuat otomatis setelah dia bayar, lewat alur yang sama dengan
                pendaftaran normal.
              </p>
            </div>
          </div>
        </div>

        {/* ── Identitas ── */}
        <div style={card}>
          <p style={sectionTitle}>Identitas</p>
          <div className="row">
            <Field text="Nama Lengkap" required>
              <input style={input} value={f.name} onChange={set("name")} />
            </Field>
            <Field text="Email" required>
              <input style={input} type="email" value={f.email} onChange={set("email")} />
            </Field>
            <Field text="Student ID" required>
              <input
                style={{ ...input, backgroundColor: autoId ? "#f3f3f3" : "#fff", color: autoId ? "#888" : "#000" }}
                value={autoId ? idPreview?.studentId || "menghitung..." : f.studentId}
                onChange={set("studentId")}
                disabled={autoId}
                placeholder="mis. 26100021"
              />
              <label style={{ fontSize: "12px", fontWeight: 600, cursor: "pointer", marginTop: "6px", display: "block" }}>
                <input
                  type="checkbox"
                  checked={autoId}
                  onChange={(e) => setAutoId(e.target.checked)}
                  style={{ marginRight: "6px" }}
                />
                Generate otomatis dari batch berjalan
              </label>
              {autoId && idPreview && (
                <p style={{ fontSize: "11px", color: "#888", margin: "4px 0 0" }}>
                  Batch {idPreview.batch} → prefix {idPreview.yearCode}
                  {f.program === "1 Year Program" ? "10" : "01"}. Angka final ditentukan saat disimpan.
                </p>
              )}
            </Field>
          </div>

          <p style={{ fontSize: "12px", color: "#888", marginBottom: "10px" }}>
            Hanya tiga field di atas yang wajib. Sisanya boleh dikosongkan — kolom yang tidak boleh kosong di database
            akan diisi tanda <code>-</code> supaya bisa dilengkapi belakangan lewat Edit Student.
          </p>

          <div className="row">
            <Field text="No. Telp">
              <input style={input} value={f.phoneNumber} onChange={set("phoneNumber")} />
            </Field>
            <Field text="Tanggal Lahir">
              <DateInput className="" style={input} value={f.dateOfBirth} onChange={set("dateOfBirth")} />
            </Field>
            <Field text="Ukuran Kaos">
              <select style={input} value={f.tshirtSize} onChange={set("tshirtSize")}>
                <option value="">-</option>
                {["XS", "S", "M", "L", "XL", "XXL"].map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {dupWarning && (
            <div
              style={{
                backgroundColor: "#fffaf0",
                border: "1px solid #f2e2b8",
                borderRadius: "6px",
                padding: "10px 12px",
                fontSize: "13px",
              }}
            >
              <p style={{ marginBottom: "8px" }}>{dupWarning}</p>
              <label style={{ fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={allowDupId}
                  onChange={(e) => setAllowDupId(e.target.checked)}
                  style={{ marginRight: "6px" }}
                />
                Izinkan Student ID ganda — saya memang sengaja
              </label>
            </div>
          )}
        </div>

        {/* ── Alamat ── */}
        <div style={card}>
          <p style={sectionTitle}>Alamat</p>
          <div className="row">
            <Field text="Alamat">
              <input style={input} value={f.address} onChange={set("address")} />
            </Field>
            <Field text="Kota">
              <input style={input} value={f.city} onChange={set("city")} />
            </Field>
            <Field text="Provinsi">
              <input style={input} value={f.province} onChange={set("province")} />
            </Field>
            <Field text="Kode Pos">
              <input
                style={input}
                value={f.postalCode}
                onChange={(e) => setF((p) => ({ ...p, postalCode: e.target.value.replace(/\D/g, "") }))}
              />
            </Field>
            <Field text="Student Package">
              <select style={input} value={f.studentPackage} onChange={set("studentPackage")}>
                <option value="">-</option>
                <option value="The House Convention Hall">The House Convention Hall</option>
                <option value="Shipping">Shipping (luar Bandung)</option>
              </select>
            </Field>
          </div>
        </div>

        {/* ── Gereja & Peran ── */}
        <div style={card}>
          <p style={sectionTitle}>Gereja &amp; Peran</p>
          <div className="row">
            <Field text="Church">
              <select
                style={input}
                value={f.churchChoice}
                onChange={(e) => setF((p) => ({ ...p, churchChoice: e.target.value, role: "", lastDj: "" }))}
              >
                <option value="IFGF Bandung">IFGF Bandung</option>
                <option value="Others">Others</option>
              </select>
            </Field>
            {f.churchChoice === "Others" && (
              <Field text="Nama Local Church">
                <input style={input} value={f.otherChurch} onChange={set("otherChurch")} />
              </Field>
            )}
            <Field text="Last DJ Class">
              <select style={input} value={f.lastDj} onChange={set("lastDj")}>
                <option value="">-</option>
                {["Come", "Grow", "Serve", "Lead", "None"].map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </Field>
            <Field text="Role">
              <select style={input} value={f.role} onChange={set("role")}>
                <option value="">-</option>
                {(f.churchChoice === "IFGF Bandung" ? ROLE_IFGF : ROLE_OTHERS).map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        {/* ── Program & Kelas ── */}
        <div style={card}>
          <p style={sectionTitle}>Program &amp; Kelas</p>
          <div className="row">
            <Field text="Program">
              <select style={input} value={f.program} onChange={set("program")}>
                <option value="1 Year Program">1 Year Program</option>
                <option value="1 Month Course">1 Month Course</option>
              </select>
            </Field>
            <Field text="Online / Onsite">
              <select style={input} value={f.classType} onChange={set("classType")}>
                <option value="Onsite">Onsite</option>
                <option value="Online">Online</option>
              </select>
            </Field>
            {isYearly && (
              <Field text="Starting Class">
                <select style={input} value={startingClass} onChange={(e) => setStartingClass(e.target.value)}>
                  {YEARLY_CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: "11px", color: "#888", margin: "4px 0 0" }}>
                  Kelas pertama yang diikuti student. Kelas sebelum ini jadwalnya sudah lewat, jadi otomatis digeser ke
                  batch berikutnya — aturan yang sama dengan pendaftaran normal.
                </p>
              </Field>
            )}
            <Field text="Pernah ikut CEA sebelumnya">
              <select style={input} value={f.joinedCEA} onChange={set("joinedCEA")}>
                <option value="">-</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
            <Field text="Scholarship Type">
              <select style={input} value={f.scholarshipType} onChange={set("scholarshipType")}>
                {SCHOLARSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            {/* Status pembayaran hanya relevan di mode arsip — di mode billed
                statusnya ditentukan oleh pembayaran yang sesungguhnya. */}
            {mode === "archive" ? (
              <Field text="Status Pembayaran">
                <select style={input} value={f.paymentStatus} onChange={set("paymentStatus")}>
                  <option value="unpaid">unpaid — masih bisa ditagih lewat cicilan</option>
                  <option value="paid">paid — sudah beres di luar sistem</option>
                </select>
              </Field>
            ) : (
              <>
                <Field text="Rencana Pembayaran">
                  <select style={input} value={f.paymentPlan} onChange={set("paymentPlan")}>
                    <option value="full">Full payment</option>
                    <option value="installment">Installment (cicilan)</option>
                  </select>
                </Field>
                <Field text="Diskon (%)">
                  <input
                    style={input}
                    value={f.discountPercent}
                    onChange={(e) => setF((p) => ({ ...p, discountPercent: e.target.value.replace(/\D/g, "") }))}
                    placeholder="0"
                  />
                </Field>
              </>
            )}
          </div>

          {isYearly ? (
            <div
              style={{
                backgroundColor: "#f0fbf8",
                border: "1px solid #cfe9e2",
                borderRadius: "6px",
                padding: "10px 12px",
                marginTop: "6px",
                fontSize: "13px",
                color: "#555",
              }}
            >
              <strong>Kelas tidak perlu dipilih.</strong> Paket 1 Year Program selalu mencakup seluruh{" "}
              {YEARLY_CLASSES.length} kelas, sama seperti pendaftaran normal:
              <div style={{ marginTop: "6px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {YEARLY_CLASSES.map((c) => (
                  <span
                    key={c}
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #cfe9e2",
                      borderRadius: "4px",
                      padding: "2px 8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#0d7a68",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
              <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#888" }}>
                Semuanya masuk ke batch berjalan{idPreview?.batch ? ` (batch ${idPreview.batch})` : ""}. Kalau perlu
                kelas tertentu digeser ke batch lain, ubah lewat Edit Student setelah tersimpan.
              </p>
            </div>
          ) : (
            <>
              <p style={{ ...label, marginTop: "10px" }}>
                Kelas yang diambil <span style={{ color: "#fb3104" }}>*</span>
              </p>
              <p style={{ fontSize: "12px", color: "#888" }}>
                Wajib minimal satu kelas. Grid absensi dan raport menyaring student lewat daftar kelas ini — tanpa
                kelas, student tidak akan muncul di keduanya.
                {mode === "billed" && " Di mode ini, kelas yang dipilih juga yang dihitung ke harga."} Batch mengikuti
                batch berjalan; kosongkan saja kalau tidak ingin mengubahnya.
              </p>
              <div className="row">
                {CLASS_OPTIONS.map((code) => {
                  const picked = classes[code] !== undefined;
                  return (
                    <div key={code} className="col-6 col-md-4 col-lg-3 mb-2">
                      <div
                        style={{
                          border: picked ? "1px solid #0d9488" : "1px solid #ddd",
                          backgroundColor: picked ? "#f0fbf8" : "#fff",
                          borderRadius: "6px",
                          padding: "8px 10px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            marginBottom: picked ? "6px" : 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={picked}
                            onChange={() => toggleClass(code)}
                            style={{ marginRight: "6px" }}
                          />
                          {code}
                        </label>
                        {picked && (
                          <input
                            style={{ ...input, height: "2rem", fontSize: "12px" }}
                            // Hanya angka: grid absensi mencocokkan batch secara
                            // exact ("1"), jadi "batch 1" membuat student tidak
                            // muncul di kelas mana pun.
                            inputMode="numeric"
                            placeholder={idPreview?.batch ? `${idPreview.batch} (angka saja)` : "angka saja"}
                            value={classes[code]}
                            onChange={(e) =>
                              setClasses((p) => ({ ...p, [code]: e.target.value.replace(/\D/g, "") }))
                            }
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Pertanyaan ── */}
        <div style={card}>
          <p style={sectionTitle}>Pertanyaan</p>
          <p style={{ fontSize: "12px", color: "#888", marginTop: "-8px" }}>
            Semuanya opsional — boleh dikosongkan dan dilengkapi belakangan lewat Edit Student.
          </p>
          {GENERAL_QUESTIONS.map((q, i) => (
            <div key={i} className="mb-3">
              <label style={label}>
                {i + 1}. {q}
              </label>
              <textarea
                style={textarea}
                value={answers[i]}
                onChange={(e) =>
                  setAnswers((p) => {
                    const next = [...p];
                    next[i] = e.target.value;
                    return next;
                  })
                }
              />
            </div>
          ))}
        </div>

        {/* ── Pertanyaan beasiswa ── */}
        {/* Muncul hanya kalau jenis beasiswa dipilih. Set pertanyaannya BEDA
            per jenis: merit 7, need-based 5 — mengikuti ApplyScholarship.js. */}
        {schQuestions.length > 0 && (
          <div style={card}>
            <p style={sectionTitle}>
              Pertanyaan Beasiswa —{" "}
              {f.scholarshipType === "merit" ? "Leadership Scholarship" : "Opportunity Scholarship"}
            </p>
            <div
              style={{
                backgroundColor: "#fff8f7",
                border: "1px solid #fbd5cc",
                borderRadius: "6px",
                padding: "10px 12px",
                marginBottom: "14px",
                fontSize: "12px",
                color: "#666",
              }}
            >
              Jawaban ini tersimpan di tabel beasiswa dengan status <strong>approved</strong> — bukan sebagai lamaran
              baru, karena beasiswanya sudah Anda putuskan. Jadi barisnya tidak akan muncul di antrean review beasiswa.
            </div>
            {schQuestions.map((q, i) => (
              <div key={i} className="mb-3">
                <label style={label}>
                  {i + 1}. {q}
                </label>
                <textarea
                  style={textarea}
                  value={schAnswers[i]}
                  onChange={(e) =>
                    setSchAnswers((p) => {
                      const next = [...p];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                />
              </div>
            ))}
            <div className="row">
              <Field text="Nama Referensi">
                <input style={input} value={refName} onChange={(e) => setRefName(e.target.value)} />
              </Field>
              <Field text="Kontak Referensi">
                <input style={input} value={refContact} onChange={(e) => setRefContact(e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        <div className="adm-card" style={{ position: "sticky", bottom: "16px", zIndex: 5 }}>
          <div className="adm-card-body d-flex justify-content-end gap-2" style={{ padding: "14px 18px" }}>
            <button type="button" className="adm-btn adm-btn--secondary" onClick={() => navigate("/studentdatapage")} disabled={saving}>
              Batal
            </button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={handleSubmit} disabled={saving}>
              {saving ? "Menyimpan..." : mode === "billed" ? "Simpan & Kirim Link Pembayaran" : "Simpan Student"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudentPage;
